import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

export interface EpssHealthData {
  status: 'ok' | 'stale' | 'down'
  lastRefreshedAt: string | null
  ageHours: number | null
  sampleSize: number
}

export interface EpssHealthCardProps {
  health: EpssHealthData | null
  flagEnabled: boolean | null
}

function formatAge(ageHours: number | null): string {
  if (ageHours === null) return 'never'
  if (ageHours < 1) return 'just now'
  if (ageHours < 24) return `${Math.round(ageHours)}h ago`
  return `${Math.floor(ageHours / 24)}d ago`
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC'
}

const STATUS_MAP = {
  ok:    { label: 'OK',    dot: 'bg-green-500', ring: 'ring-green-200' },
  stale: { label: 'Stale', dot: 'bg-amber-500', ring: 'ring-amber-200' },
  down:  { label: 'Down',  dot: 'bg-red-500',   ring: 'ring-red-200'   },
} as const

export function EpssHealthCard({ health, flagEnabled }: EpssHealthCardProps) {
  if (!health || flagEnabled === null) {
    return (
      <Card>
        <CardHeader><CardTitle>EPSS Refresh</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </CardContent>
      </Card>
    )
  }

  const meta = STATUS_MAP[health.status]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          EPSS Refresh
          <span className={`inline-block h-2.5 w-2.5 rounded-full ring-2 ${meta.dot} ${meta.ring}`} aria-hidden />
          <span className="text-sm font-normal text-muted-foreground">{meta.label}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div>
          <span className="text-muted-foreground">Last run </span>
          <span className="font-medium">{formatTimestamp(health.lastRefreshedAt)}</span>
          <span className="ml-2 text-muted-foreground">({formatAge(health.ageHours)})</span>
        </div>
        <div>
          <span className="text-muted-foreground">Coverage </span>
          <span className="font-medium">{health.sampleSize.toLocaleString()} CVEs with scores</span>
        </div>
        <div>
          <span className="text-muted-foreground">Flag </span>
          <span className="font-mono text-xs">V4_EPSS_REFRESH_ENABLED={String(flagEnabled)}</span>
          {!flagEnabled && (
            <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              Flag is OFF — refresh intentionally disabled
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
